/**
 * Image Protection Utilities
 * Prevents downloading, viewing source, and copying images
 */

/**
 * Detect if DevTools is open
 */
export const detectDevTools = () => {
  const threshold = 160;
  const widthThreshold = window.outerWidth - window.innerWidth > threshold;
  const heightThreshold = window.outerHeight - window.innerHeight > threshold;
  return widthThreshold || heightThreshold;
};

/**
 * Disable right-click globally
 */
export const disableRightClick = () => {
  document.addEventListener('contextmenu', (e) => {
    if (!e.target) return;
    if (e.target.tagName === 'IMG' || (typeof e.target.closest === 'function' && e.target.closest('.protected-image-container'))) {
      e.preventDefault();
      return false;
    }
  });
};

/**
 * Disable keyboard shortcuts for saving/copying
 */
export const disableKeyboardShortcuts = () => {
  document.addEventListener('keydown', (e) => {
    // Check if target is an image or in protected container
    if (!e.target) return;
    const isProtected = e.target.tagName === 'IMG' || 
                       (typeof e.target.closest === 'function' && e.target.closest('.protected-image-container')) ||
                       document.activeElement?.tagName === 'IMG';

    if (isProtected) {
      // Prevent Ctrl+S (Save)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+C (Copy)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+P (Print)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        return false;
      }
    }

    // Prevent F12 and DevTools shortcuts globally
    if (e.key === 'F12') {
      e.preventDefault();
      return false;
    }
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) {
      e.preventDefault();
      return false;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
      e.preventDefault();
      return false;
    }
  });
};

/**
 * Disable drag and drop for images
 */
export const disableDragDrop = () => {
  document.addEventListener('dragstart', (e) => {
    if (!e.target) return;
    if (e.target.tagName === 'IMG' || (typeof e.target.closest === 'function' && e.target.closest('.protected-image-container'))) {
      e.preventDefault();
      return false;
    }
  });
};

/**
 * Disable text selection on images
 */
export const disableSelection = () => {
  document.addEventListener('selectstart', (e) => {
    if (!e.target) return;
    if (e.target.tagName === 'IMG' || (typeof e.target.closest === 'function' && e.target.closest('.protected-image-container'))) {
      e.preventDefault();
      return false;
    }
  });
};

/**
 * Monitor for DevTools and blur images
 */
export const monitorDevTools = (callback) => {
  let devToolsOpen = false;
  
  const check = () => {
    const isOpen = detectDevTools();
    if (isOpen !== devToolsOpen) {
      devToolsOpen = isOpen;
      if (callback) callback(isOpen);
      
      // Blur or hide images when DevTools is open
      const images = document.querySelectorAll('.protected-image-container img');
      images.forEach(img => {
        if (isOpen) {
          img.style.filter = 'blur(20px)';
          img.style.opacity = '0.3';
        } else {
          img.style.filter = '';
          img.style.opacity = '';
        }
      });
    }
  };

  // Check every second
  const interval = setInterval(check, 1000);
  
  return () => clearInterval(interval);
};

/**
 * Convert image to base64 with watermark
 * Makes it harder to extract original image
 */
export const addWatermark = async (imageUrl, watermarkText = '© Protected') => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Add watermark
      ctx.font = '20px Arial';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.textAlign = 'right';
      ctx.fillText(watermarkText, canvas.width - 10, canvas.height - 10);
      
      // Convert to base64
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    
    img.onerror = reject;
    img.src = imageUrl;
  });
};

/**
 * Initialize all protection measures
 */
export const initializeImageProtection = () => {
  disableRightClick();
  disableKeyboardShortcuts();
  disableDragDrop();
  disableSelection();
  
  // Monitor DevTools (silent - no console warnings)
  const cleanup = monitorDevTools((isOpen) => {
    // Silent monitoring - don't reveal detection
  });
  
  return cleanup;
};

/**
 * Protect specific image element
 */
export const protectImage = (imgElement) => {
  if (!imgElement) return;
  
  imgElement.setAttribute('draggable', 'false');
  imgElement.style.userSelect = 'none';
  imgElement.style.pointerEvents = 'none';
  imgElement.classList.add('protected');
  
  // Prevent context menu
  imgElement.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    return false;
  });
  
  // Prevent drag
  imgElement.addEventListener('dragstart', (e) => {
    e.preventDefault();
    return false;
  });
  
  // Prevent selection
  imgElement.addEventListener('selectstart', (e) => {
    e.preventDefault();
    return false;
  });
};

/**
 * Obfuscate image URL
 * Makes it harder to find in network tab
 */
export const obfuscateImageUrl = (url) => {
  // Add random query parameter
  const randomParam = `?t=${Date.now()}&r=${Math.random().toString(36).substring(7)}`;
  return url + randomParam;
};

/**
 * Check if user is trying to inspect element
 */
export const detectInspect = () => {
  let devToolsOpen = false;
  
  const element = new Image();
  Object.defineProperty(element, 'id', {
    get: function() {
      devToolsOpen = true;
      throw new Error('DevTools detected');
    }
  });
  
  console.log(element);
  return devToolsOpen;
};

export default {
  detectDevTools,
  disableRightClick,
  disableKeyboardShortcuts,
  disableDragDrop,
  disableSelection,
  monitorDevTools,
  addWatermark,
  initializeImageProtection,
  protectImage,
  obfuscateImageUrl,
  detectInspect
};
